import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  Check,
  ClipboardCheck,
  Clock3,
  ShieldCheck,
  Stethoscope,
  UserRound,
  Zap,
} from 'lucide-react';
import SiteLayout from '@/components/site/SiteLayout';

const facilityPoints = [
  'Rigorous vetting and credentialing process',
  'Seamless integration with existing workflows',
  '24/7 operational support and analytics',
];

const physicianPoints = [
  'Complete control over schedule and workload',
  'Transparent compensation and fast payouts',
  'Malpractice coverage and administrative support',
];

const heroHeadingClassName =
  'mt-5 max-w-lg text-[2.15rem] font-black leading-[0.95] tracking-tight text-slate-950 md:text-[3.35rem] md:leading-[0.92]';

const coverageModels = [
  {
    tab: 'Virtual Coverage',
    badge: 'Where Permitted',
    headline: 'Virtual Physician Coverage for Imaging Centers',
    subheadline:
      'Secure virtual physician coverage for contrast supervision when permitted by CMS, state regulations, and facility policy.',
    features: [
      {
        title: 'Virtual Supervision',
        body: 'Coverage support when remote physician supervision is allowed.',
      },
      {
        title: 'Real-Time Contrast Guidance',
        body: 'Physician support for contrast-related questions and clinical concerns.',
      },
    ],
    ctaLabel: 'Learn about Virtual Coverage',
    href: '/virtual-coverage',
    image: '/images/virtual-radiology-coverage.png',
    imageAlt: 'Physician reviewing imaging studies remotely',
    status: 'Virtual Coverage Ready',
  },
  {
    tab: 'Onsite Support',
    badge: 'High-Touch Coverage',
    headline: 'Onsite Physician Coverage When Presence Matters',
    subheadline:
      'Place licensed, ACLS-trained physicians at your imaging center for contrast supervision, medication support, and emergency response.',
    features: [
      {
        title: 'ACLS-Trained Physicians',
        body: 'Prepared to respond to contrast reactions and urgent clinical concerns.',
      },
      {
        title: 'Flexible Site Coverage',
        body: 'Support for evenings, weekends, holidays, and expandable AM coverage.',
      },
    ],
    ctaLabel: 'Learn about Onsite Coverage',
    href: '/onsite-coverage',
    image: '/images/onsite-radiology-coverage.png',
    imageAlt: 'Modern imaging center procedure room',
    status: 'Onsite Physician Scheduled',
  },
  {
    tab: 'Hybrid Model',
    badge: 'Flexible Coverage Model',
    headline: 'A Coverage Model Built Around Your Center',
    subheadline:
      'Combine onsite physician coverage and virtual support based on your imaging schedule, facility policy, state rules, and operational needs.',
    features: [
      {
        title: 'Customized Coverage Plan',
        body: "Use onsite, virtual, or both depending on your center's workflow.",
      },
      {
        title: 'Scale Across Locations',
        body: 'Support single-site centers or multi-location imaging groups.',
      },
    ],
    ctaLabel: 'Explore Hybrid Coverage',
    href: '/hybrid-coverage',
    image: '/images/hybrid-radiology-coverage.png',
    imageAlt: 'Modern imaging center room for coordinated coverage',
    status: 'Coverage Plan Active',
  },
  {
    tab: 'Teleradiology',
    badge: 'Reading Support',
    headline: 'Teleradiology Support Beyond Contrast Coverage',
    subheadline:
      'Coordinate radiology reading support for imaging centers that need STAT reads, after-hours coverage, overflow help, or subspecialty interpretation.',
    features: [
      {
        title: 'STAT & After-Hours Reads',
        body: 'Support for urgent studies and coverage outside routine business hours.',
      },
      {
        title: 'Diagnostic & Subspecialty Support',
        body: 'reading support based on study type, volume, and specialty needs.',
      },
    ],
    ctaLabel: 'Learn about Teleradiology Support',
    href: '/teleradiology',
    image: '/images/teleradiology-reading-support.png',
    imageAlt: 'Physician reviewing imaging studies for teleradiology support',
    status: 'Reading Support Online',
  },
];

const workflowSteps = [
  {
    title: 'Share Your Coverage Need',
    body: 'Tell us what you need: onsite, virtual, hybrid, evening, weekend, holiday, one shift per week, broader ongoing coverage, or teleradiology reading support.',
  },
  {
    title: 'Build the Right Coverage Model',
    body: 'We help design the right setup for your facility: onsite physician coverage, virtual support where permitted, hybrid coverage, coordinated teleradiology reading, or a combination of these.',
  },
  {
    title: 'Launch Coverage Quickly',
    body: 'DocMoonlight helps coordinate onboarding, scheduling, and coverage logistics so your team can start faster without long hiring cycles or unnecessary back-and-forth.',
  },
  {
    title: 'Track Coverage in Real Time',
    body: 'Your team gets dashboard access to monitor coverage, schedules, activity, and contrast-related events at no extra cost.',
  },
];

const AboutPage = () => {
  const [activeModelIndex, setActiveModelIndex] = useState(0);
  const activeModel = coverageModels[activeModelIndex] || coverageModels[0];

  return (
    <SiteLayout
      title='About DocMoonlight | Physician Coverage for Healthcare Facilities'
      description='Learn how DocMoonlight supports imaging centers, infusion centers, and residents with verified onsite, virtual, hybrid, and teleradiology coverage.'
      keywords={[
        'DocMoonlight',
        'physician coverage company',
        'imaging center coverage',
        'healthcare staffing',
      ]}
    >
      <section className='px-4 pb-16 pt-8 sm:px-6 lg:px-8'>
        <div className='mx-auto grid max-w-7xl overflow-hidden rounded-[2.6rem] border border-slate-200 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.08)] lg:grid-cols-2'>
          <div className='relative overflow-hidden bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.1),transparent_32%),linear-gradient(180deg,#ffffff_0%,#fbfdff_100%)] px-7 py-14 md:px-12 lg:py-20'>
            <div className='inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-[0.66rem] font-black uppercase tracking-[0.16em] text-[#12315f]'>
              <span className='h-1.5 w-1.5 rounded-full bg-blue-500' />
              For Facilities
            </div>
            <h1 className={heroHeadingClassName}>
              Coverage when your patients need it most.
            </h1>
            <p className='mt-5 max-w-md text-[0.98rem] font-semibold leading-7 text-slate-600'>
              Connect with top-tier, verified physicians to ensure seamless care
              continuity and operational excellence.
            </p>

            <div className='mt-8 flex flex-wrap items-center gap-4'>
              <Link
                href='/what-we-offer#coverage-form'
                className='inline-flex min-h-12 items-center rounded-xl bg-[#f59e0b] px-6 text-sm font-black text-white shadow-[0_14px_30px_rgba(245,158,11,0.22)] transition hover:-translate-y-1'
              >
                Request Coverage
              </Link>
            </div>
          </div>

          <div className='relative overflow-hidden bg-[radial-gradient(circle_at_80%_28%,rgba(245,158,11,0.14),transparent_34%),linear-gradient(180deg,#fffaf4_0%,#ffffff_100%)] px-7 py-14 md:px-12 lg:py-20'>
            <div className='inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1.5 text-[0.66rem] font-black uppercase tracking-[0.16em] text-[#9a5f00]'>
              <span className='h-1.5 w-1.5 rounded-full bg-[#f59e0b]' />
              For Physicians
            </div>
            <h1 className={heroHeadingClassName}>
              Flexible opportunities that fit your schedule.
            </h1>
            <p className='mt-5 max-w-md text-[0.98rem] font-semibold leading-7 text-slate-600'>
              Take control of your career with premium moonlight shifts that
              align with your expertise and availability.
            </p>
            <div className='mt-8 flex flex-wrap items-center gap-4'>
              <Link
                href='/residents'
                className='inline-flex min-h-12 items-center rounded-xl bg-[#9a6a17] px-6 text-sm font-black text-white shadow-[0_14px_30px_rgba(154,106,23,0.22)] transition hover:-translate-y-1'
              >
                Join DocMoonlight
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className='px-4 py-12 sm:px-6 lg:px-8'>
        <div className='mx-auto max-w-7xl'>
          <div className='mx-auto max-w-3xl text-center'>
            <h2 className='text-[1.65rem] font-black tracking-tight text-slate-950 md:text-[2.65rem]'>
              One Platform. Two Missions.
            </h2>
            <p className='mt-3 text-sm font-semibold leading-7 text-slate-600 md:text-base'>
              Bridging the gap between healthcare facilities needing reliable
              coverage and physicians seeking flexible opportunities.
            </p>
          </div>

          <div className='mt-12 grid gap-7 lg:grid-cols-2'>
            <article className='rounded-[1.6rem] border border-[#f0c36b] bg-white p-8 shadow-[0_24px_70px_rgba(15,23,42,0.1)]'>
              <div className='inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#111b35] text-white shadow-[0_12px_24px_rgba(15,23,42,0.18)]'>
                <Building2 className='h-5 w-5' />
              </div>
              <h3 className='mt-7 text-xl font-black text-slate-950'>
                Empowering Facilities
              </h3>
              <p className='mt-4 max-w-xl text-sm font-semibold leading-7 text-slate-600'>
                We provide healthcare centers with immediate access to a vetted
                network of specialized physicians, ensuring that patient care
                never stops.
              </p>
              <div className='mt-6 space-y-3'>
                {facilityPoints.map((point) => (
                  <div
                    key={point}
                    className='flex items-center gap-3 text-sm font-black text-slate-700'
                  >
                    <span className='inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#111b35] text-white'>
                      <Check className='h-3 w-3' />
                    </span>
                    {point}
                  </div>
                ))}
              </div>
            </article>

            <article className='rounded-[1.6rem] border border-[#f0c36b] bg-white p-8 shadow-[0_24px_70px_rgba(15,23,42,0.1)]'>
              <div className='inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[linear-gradient(180deg,#c48a22_0%,#8d641e_100%)] text-white shadow-[0_12px_24px_rgba(154,106,23,0.2)]'>
                <Stethoscope className='h-5 w-5' />
              </div>
              <h3 className='mt-7 text-xl font-black text-slate-950'>
                Elevating Physicians
              </h3>
              <p className='mt-4 max-w-xl text-sm font-semibold leading-7 text-slate-600'>
                We empower doctors to take charge of their careers with
                flexible, high-reward opportunities that respect their time and
                expertise.
              </p>
              <div className='mt-6 space-y-3'>
                {physicianPoints.map((point) => (
                  <div
                    key={point}
                    className='flex items-center gap-3 text-sm font-black text-slate-700'
                  >
                    <span className='inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#b27b1f] text-white'>
                      <Check className='h-3 w-3' />
                    </span>
                    {point}
                  </div>
                ))}
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* <section
        id='videos'
        className='bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] px-4 py-16 sm:px-6 lg:px-8'
      >
        <div className='mx-auto grid max-w-7xl gap-8 lg:grid-cols-2'>
          {[
            {
              title: 'The DocMoonlight Story',
              src: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
            },
            {
              title: 'How the Platform Works',
              src: 'https://www.youtube.com/embed/C1lxHDlCoI8',
            },
          ].map((video) => (
            <article key={video.title}>
              <h3 className='mb-5 text-lg font-black text-slate-950'>
                {video.title}
              </h3>
              <div className='overflow-hidden rounded-[1.35rem] border border-slate-300 bg-slate-950 shadow-[0_20px_45px_rgba(15,23,42,0.16)]'>
                <div className='aspect-video'>
                  <iframe
                    className='h-full w-full'
                    src={video.src}
                    title={video.title}
                    loading='lazy'
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                    allowFullScreen
                  />
                </div>
              </div>
            </article>
          ))}
        </div>
      </section> */}

      <section className='bg-[linear-gradient(180deg,#f5f8fb_0%,#eef6f6_100%)] px-4 py-20 sm:px-6 lg:px-8'>
        <div className='mx-auto max-w-7xl'>
          <div className='mx-auto max-w-3xl text-center'>
            <h2 className='text-[1.3rem] font-black tracking-tight text-slate-950 md:text-[2.65rem]'>
              Comprehensive Coverage Models
            </h2>
            <p className='mt-3 text-sm font-semibold leading-7 text-slate-600 md:text-base'>
              Tailored solutions to meet the specific operational needs of your
              facility.
            </p>
          </div>

          <div className='mt-12 overflow-hidden rounded-[1.6rem] border border-[#e5aa35] bg-white shadow-[0_30px_80px_rgba(15,23,42,0.14)]'>
            <div className='flex flex-wrap border-b border-slate-200 text-xs font-black text-slate-600'>
              {coverageModels.map((model, index) => (
                <button
                  key={model.tab}
                  type='button'
                  onClick={() => setActiveModelIndex(index)}
                  aria-pressed={activeModelIndex === index}
                  className={`min-h-14 px-7 ${
                    activeModelIndex === index
                      ? 'border-b-2 border-[#f59e0b] text-[#111b35]'
                      : 'transition hover:text-[#111b35]'
                  }`}
                >
                  {model.tab}
                </button>
              ))}
            </div>

            <div className='grid gap-10 p-7 md:p-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center'>
              <div>
                <div className='inline-flex items-center gap-2 rounded-full bg-[#111b35] px-3 py-1.5 text-[0.62rem] font-black uppercase tracking-[0.16em] text-white'>
                  <Zap className='h-3 w-3 text-[#f59e0b]' />
                  {activeModel.badge}
                </div>
                <h3 className='mt-6 text-[1.5rem] font-black leading-tight text-slate-950 md:text-[2.2rem]'>
                  {activeModel.headline}
                </h3>
                <p className='mt-4 max-w-xl text-sm font-semibold leading-7 text-slate-600'>
                  {activeModel.subheadline}
                </p>

                <div className='mt-8 space-y-4'>
                  {activeModel.features.map((feature, index) => {
                    const FeatureIcon = index === 0 ? Clock3 : ShieldCheck;

                    return (
                      <div
                        key={feature.title}
                        className='flex items-center gap-4 rounded-xl bg-blue-50 px-5 py-4'
                      >
                        <span className='inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#111b35] text-white'>
                          <FeatureIcon className='h-4 w-4' />
                        </span>
                        <div>
                          <div className='text-sm font-black text-slate-950'>
                            {feature.title}
                          </div>
                          <div className='text-xs font-semibold leading-5 text-slate-500'>
                            {feature.body}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Link
                  href={activeModel.href}
                  className='mt-8 inline-flex items-center gap-2 text-sm font-black text-[#111b35]'
                >
                  {activeModel.ctaLabel}
                  <ArrowRight className='h-4 w-4' />
                </Link>
              </div>

              <div className='relative'>
                <div className='relative h-[20rem] overflow-hidden rounded-[1.4rem] border border-slate-200 shadow-[0_20px_45px_rgba(15,23,42,0.12)]'>
                  <Image
                    src={activeModel.image}
                    alt={activeModel.imageAlt}
                    fill
                    sizes='(max-width: 1023px) 100vw, 42vw'
                    className='object-cover'
                  />
                </div>
                <div className='absolute bottom-8 left-[-1rem] rounded-2xl bg-white p-4 shadow-[0_18px_40px_rgba(15,23,42,0.18)]'>
                  <div className='flex items-center gap-3'>
                    <span className='inline-flex h-11 w-11 items-center justify-center rounded-full bg-[linear-gradient(180deg,#c8ced8_0%,#7f8795_100%)] text-white'>
                      <Check className='h-5 w-5' />
                    </span>
                    <div>
                      <div className='text-[0.62rem] font-black uppercase tracking-[0.16em] text-slate-500'>
                        Status
                      </div>
                      <div className='text-sm font-black text-slate-950'>
                        {activeModel.status}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='px-4 py-14 sm:px-6 sm:py-20 lg:px-8'>
        <div className='mx-auto max-w-5xl'>
          <div className='text-center'>
            <h2 className='text-[1.45rem] font-black tracking-tight text-slate-950 sm:text-[2rem] md:text-[2.65rem]'>
              How DocMoonlight Works
            </h2>
            <p className='mt-3 text-sm font-semibold leading-7 text-slate-600 md:text-base'>
              A simple, flexible process designed to help facilities scale
              coverage quickly with physician coverage and coordinated
              teleradiology support in one model.
            </p>
          </div>

          <div className='relative mx-auto mt-9 max-w-3xl sm:mt-16'>
            <div className='absolute left-[1.05rem] top-0 h-full w-px bg-[#d79622] md:left-1/2 md:-translate-x-1/2' />
            <div className='space-y-4 sm:space-y-10'>
              {workflowSteps.map((step, index) => {
                const isLeft = index % 2 === 0;
                const isLast = index === workflowSteps.length - 1;

                return (
                  <div
                    key={step.title}
                    className='relative grid grid-cols-[2.15rem_1fr] gap-2.5 md:grid-cols-[1fr_4rem_1fr] md:items-start md:gap-5'
                  >
                    <div
                      className={`col-start-2 rounded-xl bg-white p-3 shadow-[0_12px_28px_rgba(15,23,42,0.06)] sm:rounded-2xl sm:p-5 md:row-start-1 md:bg-transparent md:pt-2 md:shadow-none ${
                        isLeft
                          ? 'md:col-start-1 md:text-right'
                          : 'md:col-start-3 md:text-left'
                      }`}
                    >
                      <h3 className='text-sm font-black text-slate-950 sm:text-base'>
                        {step.title}
                      </h3>
                      <p className='mt-1.5 text-xs font-semibold leading-5 text-slate-600 sm:mt-2 sm:text-sm sm:leading-6'>
                        {step.body}
                      </p>
                    </div>
                    <div className='col-start-1 row-start-1 flex justify-center pt-1 md:col-start-2 md:row-start-1 md:pt-0'>
                      <span
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-xs font-black text-white shadow-[0_10px_20px_rgba(15,23,42,0.16)] sm:h-11 sm:w-11 sm:rounded-xl sm:text-sm sm:shadow-[0_14px_26px_rgba(15,23,42,0.18)] ${
                          isLast
                            ? 'bg-[linear-gradient(180deg,#c8ced8_0%,#6f7784_100%)]'
                            : index === 2
                              ? 'bg-[linear-gradient(180deg,#a36e17_0%,#6e4a12_100%)]'
                              : 'bg-[#111b35]'
                        }`}
                      >
                        {isLast ? (
                          <Check className='h-4 w-4 sm:h-5 sm:w-5' />
                        ) : (
                          index + 1
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <p className='mx-auto mt-12 max-w-3xl text-center text-sm font-black leading-7 text-[#111b35] md:text-base'>
            Start with one shift, expand as needed, and adjust anytime. No
            long-term commitment required.
          </p>
        </div>
      </section>

      <section className='bg-[linear-gradient(180deg,#ffffff_0%,#fbfdff_100%)] px-4 py-20 text-[var(--theme-dark)] sm:px-6 lg:px-8'>
        <div className='mx-auto grid max-w-7xl gap-10 lg:grid-cols-[1fr_0.75fr] lg:items-center'>
          <div className='rounded-[1.75rem] border border-slate-200 bg-white/90 p-5 shadow-[0_16px_36px_rgba(15,23,42,0.06)] md:p-7'>
            <div className='flex gap-4'>
              <div className='w-1 shrink-0 rounded-full bg-[linear-gradient(180deg,#f59e0b_0%,#fbbf24_100%)]' />
              <div>
                <blockquote className='text-[1rem] italic leading-8 text-slate-700 md:text-[1.08rem]'>
                  &ldquo;I built DocMoonlight from real resident experience - to
                  make moonlighting more accessible for doctors in training and
                  physician coverage simpler for healthcare facilities. Our goal
                  is to help residents and fellows earn without relying on
                  closed circles, while helping facilities solve real coverage
                  gaps with flexible, physician-led support.&rdquo;
                </blockquote>
                <div className='mt-5 flex items-center gap-3'>
                  <Image
                    src='/images/founder.png'
                    alt='Ramez Morcos'
                    width={44}
                    height={44}
                    sizes='44px'
                    className='h-11 w-11 rounded-full object-cover shadow-sm'
                  />
                  <div>
                    <div className='text-sm font-black text-slate-950'>
                      Ramez Morcos, MD, MBA
                    </div>
                    <div className='text-sm text-slate-500'>Founder, CEO</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='mx-auto w-full max-w-md rounded-[1.7rem] border border-[var(--theme-shadow)] bg-[var(--theme-paper)] p-8 text-[var(--theme-dark)] shadow-[0_24px_70px_rgba(20,33,61,0.1)]'>
            <h2 className='text-2xl font-black leading-tight'>
              Ready to build your coverage model?
            </h2>
            <p className='mt-3 text-sm font-semibold leading-7 text-[var(--theme-dark-soft)]/70'>
              Whether you need physician coverage for your facility or flexible
              moonlighting as a resident or fellow, DocMoonlight can help.
            </p>
            <Link
              href='/what-we-offer#coverage-form'
              className='mt-7 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[var(--theme-dark)] text-sm font-black text-[var(--theme-paper)] shadow-[0_14px_28px_rgba(20,33,61,0.18)] transition hover:-translate-y-1 hover:bg-[var(--theme-dark-soft)]'
            >
              Request Coverage
              <ArrowRight className='h-4 w-4' />
            </Link>
            <div className='my-6 flex items-center gap-4 text-xs font-bold uppercase tracking-[0.18em] text-[var(--theme-dark-soft)]/35'>
              <span className='h-px flex-1 bg-[var(--theme-surface)]' />
              or
              <span className='h-px flex-1 bg-[var(--theme-surface)]' />
            </div>
            <Link
              href='/residents'
              className='inline-flex min-h-12 w-full items-center justify-center rounded-xl border border-[var(--theme-shadow)] bg-[var(--theme-paper-warm)] text-sm font-black text-[var(--theme-dark)] transition hover:-translate-y-1 hover:border-[var(--theme-accent)] hover:bg-[var(--theme-accent-soft)]'
            >
              Join DocMoonlight
            </Link>
            <div className='mt-6 flex justify-center gap-4 text-[0.56rem] font-black uppercase tracking-[0.12em] text-[var(--theme-dark-soft)]/50'>
              <span className='inline-flex items-center gap-1'>
                <BadgeCheck className='h-3.5 w-3.5 text-[var(--theme-accent)]' />
                Physician-Led Platform
              </span>
              <span className='inline-flex items-center gap-1'>
                <ClipboardCheck className='h-3.5 w-3.5 text-[var(--theme-dark)]' />
                Secure App-Based Workflow
              </span>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default AboutPage;
